import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./UserAccountListFacets.module.styl";

export const DEFAULT_USER_ACCOUNT_LIST_FILTERS = Object.freeze({
  status: []
});

const STATUS_OPTIONS = [
  { value: "PENDING", label: t("common.pending") },
  { value: "APPROVED", label: t("common.approved") },
  { value: "REJECTED", label: t("common.rejected") },
  { value: "ACTIVE", label: t("common.active") },
  { value: "DISABLED", label: t("common.disabled") }
];

const normalizeArray = (value) => Array.isArray(value)
  ? value.map((item) => String(item || "").trim()).filter(Boolean)
  : [];

export const normalizeUserAccountListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const allowed = new Set(STATUS_OPTIONS.map((item) => item.value));

  return {
    status: normalizeArray(input.status).map((item) => item.toUpperCase()).filter((item) => allowed.has(item))
  };
};

export const isDefaultUserAccountListFilters = (value) => normalizeUserAccountListFilters(value).status.length === 0;
export const getUserAccountListActiveFilterCount = (value) => normalizeUserAccountListFilters(value).status.length;

const quoteRsql = (value) => `"${String(value || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

export const buildUserAccountFilterRsql = (value) => {
  const filters = normalizeUserAccountListFilters(value);

  if (!filters.status.length) {
    return "";
  }

  return `status=in=(${filters.status.map(quoteRsql).join(",")})`;
};

export const combineUserAccountListRsql = (...queries) => queries.map((query) => String(query || "").trim()).filter(Boolean).join(";");

export const loadUserAccountFacetCounts = async ({ search, filters }) => {
  return await rest.request(getServiceUri() + "user-account-list/facets", "POST", {
    search: String(search || "").trim(),
    filters: normalizeUserAccountListFilters(filters)
  });
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};
  for (const key of path) current = current?.[key];
  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const FacetSection = ({ title, children }) => <section className={styles.section}><div className={styles.sectionTitle}>{title}</div><div className={styles.sectionBody}>{children}</div></section>;
const CheckboxOption = ({ label, selected, count, onClick }) => <button type="button" className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option} onClick={onClick}><span className={styles.checkBox}>{selected ? "✓" : ""}</span><span className={styles.optionLabel}>{label}</span><span className={styles.count}>{count}</span></button>;
const Chip = ({ label, onRemove }) => <button type="button" className={styles.chip} onClick={onRemove}><span>{label}</span><span className={styles.chipClose}>×</span></button>;

export const UserAccountListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;
  return <button type="button" className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton} onClick={controller.toggleDrawer}><span className={styles.filterButtonIcon}><TuneIcon fontSize="inherit" /></span><span className={styles.filterButtonLabel}>{t("common.filters")}</span>{activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}</button>;
};

export const UserAccountListFacetChips = ({ controller }) => {
  const filters = normalizeUserAccountListFilters(controller?.value);
  if (isDefaultUserAccountListFilters(filters)) return null;
  return <div className={styles.chipsBar}>{filters.status.map((value) => <Chip key={value} label={`${t("common.status")}: ${STATUS_OPTIONS.find((item) => item.value === value)?.label || value}`} onRemove={() => controller.patchValue({ status: filters.status.filter((item) => item !== value) })} />)}<button type="button" className={styles.clearAll} onClick={controller.reset}>{t("common.clearAll")}</button></div>;
};

const UserAccountListFacets = ({ controller }) => {
  const filters = normalizeUserAccountListFilters(controller?.value);
  const counts = controller?.counts || {};
  return <div className={styles.drawer}><div className={styles.drawerHeader}><div><div className={styles.drawerTitle}>{t("common.filters")}</div><div className={styles.drawerSubtitle}>{controller.countsLoading ? t("common.updatingCounts") : t("filter.refineOperators")}</div></div><button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}><CloseIcon fontSize="inherit" /></button></div><div className={styles.drawerBody}><FacetSection title={t("common.status")}>{STATUS_OPTIONS.map((option) => <CheckboxOption key={option.value} label={option.label} selected={filters.status.includes(option.value)} count={countFor(counts, ["status", option.value])} onClick={() => controller.patchValue({ status: toggleArrayValue(filters.status, option.value) })} />)}</FacetSection></div><div className={styles.drawerFooter}><button type="button" className={styles.secondaryAction} onClick={controller.reset} disabled={controller.activeCount === 0}><RestartAltIcon fontSize="inherit" /><span>{t("common.resetFilters")}</span></button><button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>{t("common.done")}</button></div></div>;
};

export const USER_ACCOUNT_LIST_FACETS = {
  storageKey: "haiti.user-account.facets.v1",
  overlayLabel: t("filter.operatorFilters"),
  defaultValue: DEFAULT_USER_ACCOUNT_LIST_FILTERS,
  normalizeValue: normalizeUserAccountListFilters,
  isDefaultValue: isDefaultUserAccountListFilters,
  getActiveCount: getUserAccountListActiveFilterCount,
  loadCounts: loadUserAccountFacetCounts,
  renderButton: (controller) => <UserAccountListFacetButton controller={controller} />,
  renderDrawer: (controller) => <UserAccountListFacets controller={controller} />,
  renderChips: (controller) => <UserAccountListFacetChips controller={controller} />
};

export default UserAccountListFacets;
